/**
 * pixi-particles — Particle.ts
 *
 * Ported from Phaser's `Phaser.GameObjects.Particles.Particle`.
 *
 * A Particle is a lightweight object owned and controlled by a ParticleEmitter.
 * It holds its own position, velocity, acceleration, rotation, scale, alpha,
 * tint and lifespan, all driven each frame by the emitter's EmitterOp
 * instances. Particles are pooled and recycled by the emitter.
 *
 * Rendering: each Particle owns one `PIXI.Particle` (`pixiParticle`), which is
 * what actually gets added to / removed from the `PIXI.ParticleContainer`.
 * `PIXI.Particle` is a different, lighter-weight class than `PIXI.Sprite` —
 * see ParticleEmitter.ts for how it's created/pooled.
 */

import type { Texture } from 'pixi.js';
import { Clamp, DegToRad, RotateAround } from './Utils';

export interface IParticleData { min: number; max: number }

export class Particle
{
    emitter: any;

    /** Fractional index into `emitter.frames`, advanced by `emitter.frameRate` each update (flipbook animation). */
    frameIndex = 0;

    texture: Texture | null = null;

    x = 0;
    y = 0;

    worldPosition: { x: number; y: number } = { x: 0, y: 0 };

    velocityX = 0;
    velocityY = 0;

    accelerationX = 0;
    accelerationY = 0;

    maxVelocityX = 10000;
    maxVelocityY = 10000;

    bounce = 0;

    scaleX = 1;
    scaleY = 1;

    alpha = 1;

    angle = 0;
    rotation = 0;

    tint = 0xffffff;

    life = 1000;
    lifeCurrent = 1000;

    delayCurrent = 0;
    holdCurrent = 0;

    lifeT = 0;

    data: Record<string, IParticleData> = {
        tint: { min: 0xffffff, max: 0xffffff },
        alpha: { min: 1, max: 1 },
        rotate: { min: 0, max: 0 },
        scaleX: { min: 1, max: 1 },
        scaleY: { min: 1, max: 1 },
        x: { min: 0, max: 0 },
        y: { min: 0, max: 0 },
        accelerationX: { min: 0, max: 0 },
        accelerationY: { min: 0, max: 0 },
        maxVelocityX: { min: 0, max: 0 },
        maxVelocityY: { min: 0, max: 0 },
        moveToX: { min: 0, max: 0 },
        moveToY: { min: 0, max: 0 },
        bounce: { min: 0, max: 0 }
    };

    /**
     * The lightweight `PIXI.Particle` render object this Particle drives.
     * Created lazily by the emitter the first time this pooled particle
     * fires, then reused for the particle's whole lifetime in the pool.
     */
    pixiParticle: any = null;

    bounds = { x: 0, y: 0, width: 0, height: 0 };

    constructor (emitter: any)
    {
        this.emitter = emitter;
    }

    emit (event: string, ...args: any[]): any
    {
        return this.emitter.emit(event, ...args);
    }

    isAlive (): boolean
    {
        return (this.lifeCurrent > 0);
    }

    kill (): void
    {
        this.lifeCurrent = 0;
    }

    setPosition (x = 0, y = 0): void
    {
        this.x = x;
        this.y = y;
    }

    /**
     * Starts this particle from the given coordinates.
     * @return `true` if alive, `false` if it spawned inside a death zone.
     */
    fire (x?: number, y?: number): boolean
    {
        var emitter = this.emitter;
        var ops = emitter.ops;

        this.frameIndex = 0;
        this.texture = emitter.getFrame();

        if (!this.texture)
        {
            throw new Error('Particle has no texture — did you call setTexture() / provide a spritesheet config?');
        }

        //  Updates particle.x and particle.y during this call
        emitter.getEmitZone(this);

        if (x === undefined)
        {
            this.x += ops.x.onEmit(this, 'x');
        }
        else if (ops.x.steps > 0)
        {
            this.x += x + ops.x.onEmit(this, 'x');
        }
        else
        {
            this.x += x;
        }

        if (y === undefined)
        {
            this.y += ops.y.onEmit(this, 'y');
        }
        else if (ops.y.steps > 0)
        {
            this.y += y + ops.y.onEmit(this, 'y');
        }
        else
        {
            this.y += y;
        }

        this.life = ops.lifespan.onEmit(this, 'lifespan');
        this.lifeCurrent = this.life;
        this.lifeT = 0;

        this.delayCurrent = ops.delay.onEmit(this, 'delay');
        this.holdCurrent = ops.hold.onEmit(this, 'hold');

        this.scaleX = ops.scaleX.onEmit(this, 'scaleX');
        this.scaleY = (ops.scaleY.active) ? ops.scaleY.onEmit(this, 'scaleY') : this.scaleX;

        this.angle = ops.rotate.onEmit(this, 'rotate');
        this.rotation = DegToRad(this.angle);

        emitter.transformPoint(this.x, this.y, this.worldPosition);

        //  Check we didn't spawn in the middle of a DeathZone
        if (this.delayCurrent === 0 && emitter.getDeathZone(this))
        {
            this.lifeCurrent = 0;

            return false;
        }

        var sx = ops.speedX.onEmit(this, 'speedX');
        var sy = (ops.speedY.active) ? ops.speedY.onEmit(this, 'speedY') : sx;

        if (emitter.radial)
        {
            var rad = DegToRad(ops.angle.onEmit(this, 'angle'));

            this.velocityX = Math.cos(rad) * Math.abs(sx);
            this.velocityY = Math.sin(rad) * Math.abs(sy);
        }
        else if (emitter.moveTo)
        {
            var mx = ops.moveToX.onEmit(this, 'moveToX');
            var my = ops.moveToY.onEmit(this, 'moveToY');
            var lifeS = this.life / 1000;

            this.velocityX = (mx - this.x) / lifeS;
            this.velocityY = (my - this.y) / lifeS;
        }
        else
        {
            this.velocityX = sx;
            this.velocityY = sy;
        }

        if (emitter.acceleration)
        {
            this.accelerationX = ops.accelerationX.onEmit(this, 'accelerationX');
            this.accelerationY = ops.accelerationY.onEmit(this, 'accelerationY');
        }

        this.maxVelocityX = ops.maxVelocityX.onEmit(this, 'maxVelocityX');
        this.maxVelocityY = ops.maxVelocityY.onEmit(this, 'maxVelocityY');

        this.bounce = ops.bounce.onEmit(this, 'bounce');

        this.alpha = ops.alpha.onEmit(this, 'alpha');

        if (ops.color.active)
        {
            this.tint = ops.color.onEmit(this, 'tint');
        }
        else
        {
            this.tint = ops.tint.onEmit(this, 'tint');
        }

        return true;
    }

    /**
     * @return `true` if this particle has now expired and should be removed.
     */
    update (delta: number, step: number, processors: any[]): boolean
    {
        var emitter = this.emitter;

        //  Spritesheet flipbook animation (game-specific extension): if the
        //  emitter has a frameRate and more than one frame, this particle
        //  advances its own fractional frame index and samples straight from
        //  `emitter.frames`, independent of the emit-time `getFrame()` pick.
        var frameRate = emitter.frameRate;

        if (frameRate && emitter.frames.length > 1)
        {
            this.frameIndex += (delta / 1000) * frameRate;

            this.texture = emitter.frames[Math.floor(this.frameIndex) % emitter.frames.length];
        }

        if (this.lifeCurrent <= 0)
        {
            if (this.holdCurrent > 0)
            {
                this.holdCurrent -= delta;

                return (this.holdCurrent <= 0);
            }
            else
            {
                return true;
            }
        }

        if (this.delayCurrent > 0)
        {
            this.delayCurrent -= delta;

            return false;
        }

        var ops = emitter.ops;

        var t = 1 - (this.lifeCurrent / this.life);

        this.lifeT = t;

        this.x = ops.x.onUpdate(this, 'x', t, this.x);
        this.y = ops.y.onUpdate(this, 'y', t, this.y);

        if (emitter.moveTo)
        {
            var mx = ops.moveToX.onUpdate(this, 'moveToX', t, emitter.moveToX);
            var my = ops.moveToY.onUpdate(this, 'moveToY', t, emitter.moveToY);
            var lifeS = this.lifeCurrent / 1000;

            this.velocityX = (mx - this.x) / lifeS;
            this.velocityY = (my - this.y) / lifeS;
        }

        this.computeVelocity(emitter, delta, step, processors, t);

        this.scaleX = ops.scaleX.onUpdate(this, 'scaleX', t, this.scaleX);

        if (ops.scaleY.active)
        {
            this.scaleY = ops.scaleY.onUpdate(this, 'scaleY', t, this.scaleY);
        }
        else
        {
            this.scaleY = this.scaleX;
        }

        this.angle = ops.rotate.onUpdate(this, 'rotate', t, this.angle);
        this.rotation = DegToRad(this.angle);

        if (emitter.getDeathZone(this))
        {
            this.lifeCurrent = 0;

            return true;
        }

        this.alpha = Clamp(ops.alpha.onUpdate(this, 'alpha', t, this.alpha), 0, 1);

        if (ops.color.active)
        {
            this.tint = ops.color.onUpdate(this, 'color', t, this.tint);
        }
        else
        {
            this.tint = ops.tint.onUpdate(this, 'tint', t, this.tint);
        }

        this.lifeCurrent -= delta;

        return (this.lifeCurrent <= 0 && this.holdCurrent <= 0);
    }

    computeVelocity (emitter: any, delta: number, step: number, processors: any[], t: number): void
    {
        var ops = emitter.ops;

        var vx = this.velocityX;
        var vy = this.velocityY;

        var ax = ops.accelerationX.onUpdate(this, 'accelerationX', t, this.accelerationX);
        var ay = ops.accelerationY.onUpdate(this, 'accelerationY', t, this.accelerationY);

        var mx = ops.maxVelocityX.onUpdate(this, 'maxVelocityX', t, this.maxVelocityX);
        var my = ops.maxVelocityY.onUpdate(this, 'maxVelocityY', t, this.maxVelocityY);

        this.bounce = ops.bounce.onUpdate(this, 'bounce', t, this.bounce);

        vx += (emitter.gravityX * step) + (ax * step);
        vy += (emitter.gravityY * step) + (ay * step);

        vx = Clamp(vx, -mx, mx);
        vy = Clamp(vy, -my, my);

        this.velocityX = vx;
        this.velocityY = vy;

        this.x += vx * step;
        this.y += vy * step;

        emitter.transformPoint(this.x, this.y, this.worldPosition);

        for (var i = 0; i < processors.length; i++)
        {
            var processor = processors[i];

            if (processor.active)
            {
                processor.update(this, delta, step, t);
            }
        }
    }

    /**
     * Returns the axis-aligned bounds of this particle in "world" space (the
     * emitter's own transform applied — see ParticleEmitter.transformPoint).
     */
    getBounds (): { x: number; y: number; width: number; height: number }
    {
        var x = this.x;
        var y = this.y;
        var rotation = this.rotation;
        var width = (this.texture ? this.texture.width * this.scaleX : 0) / 2;
        var height = (this.texture ? this.texture.height * this.scaleY : 0) / 2;

        var topLeft = { x: x - width, y: y - height };
        var topRight = { x: x + width, y: y - height };
        var bottomLeft = { x: x - width, y: y + height };
        var bottomRight = { x: x + width, y: y + height };

        if (rotation !== 0)
        {
            RotateAround(topLeft, x, y, rotation);
            RotateAround(topRight, x, y, rotation);
            RotateAround(bottomLeft, x, y, rotation);
            RotateAround(bottomRight, x, y, rotation);
        }

        [ topLeft, topRight, bottomLeft, bottomRight ].forEach((p) =>
        {
            this.emitter.transformPoint(p.x, p.y, p);
        });

        var bounds = this.bounds;

        bounds.x = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        bounds.y = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        bounds.width = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x) - bounds.x;
        bounds.height = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y) - bounds.y;

        return bounds;
    }

    destroy (): void
    {
        this.emitter = null;
        this.texture = null;
        this.pixiParticle = null;
    }
}

export default Particle;
